import React, { ReactElement, ReactNode } from 'react';
import { Message, Modal } from 'rsuite';

type FormModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
  errorSummary?: string[];
  className?: string;
};

type FormFieldProps = {
  label: string;
  id?: string;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  maxLength?: number;
  showCounter?: boolean;
  children: ReactNode;
};

type FormSectionProps = {
  title: string;
  children: ReactNode;
};

export function FormModal({
  open,
  title,
  onClose,
  footer,
  children,
  errorSummary = [],
  className,
}: FormModalProps) {
  return (
    <Modal
      className={`form-modal ${className ?? ''}`.trim()}
      open={open}
      onClose={onClose}
      size="lg"
      enforceFocus
      keyboard
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="form-modal__body">
        {errorSummary.length > 0 ? (
          <Message showIcon type="error" className="form-modal__error-summary">
            <div className="form-modal__error-list">
              {/* De-duplicate: the API can return the same message for
                  several fields (e.g. description in 3 languages), which
                  would otherwise collide as React keys. */}
              {Array.from(new Set(errorSummary)).map(message => (
                <span key={message}>{message}</span>
              ))}
            </div>
          </Message>
        ) : null}
        {children}
      </Modal.Body>
      <Modal.Footer className="form-modal__footer">{footer}</Modal.Footer>
    </Modal>
  );
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="form-modal__grid">{children}</div>;
}

export function FormSection({ title, children }: FormSectionProps) {
  return (
    <div className="form-modal__section">
      <div className="form-modal__section-title">
        <span>{title}</span>
      </div>
      {children}
    </div>
  );
}

export function FormField({
  label,
  id,
  required = false,
  error,
  hint,
  maxLength,
  showCounter = true,
  children,
}: FormFieldProps) {
  const errorId = error && id ? `${id}-error` : undefined;
  const childProps = React.isValidElement(children)
    ? children.props
    : undefined;
  const childTypeName =
    React.isValidElement(children) &&
    typeof children.type !== 'string' &&
    children.type
      ? 'displayName' in children.type &&
        typeof children.type.displayName === 'string'
        ? children.type.displayName
        : 'name' in children.type && typeof children.type.name === 'string'
          ? children.type.name
          : ''
      : '';
  const isDateField =
    /Date(?:Range)?Picker/.test(childTypeName) ||
    childProps?.type === 'date' ||
    childProps?.type === 'datetime-local';
  const isNumericField =
    /(InputNumber|NumberInput)/.test(childTypeName) ||
    childProps?.type === 'number';
  // Pickers hold an opaque value (an enum code or UUID) and rich-text
  // editors hold markup — a character count of either is meaningless.
  const isNonTextControl = /Picker|Cascader|RichText/.test(childTypeName);
  // A read-only / disabled field is not being edited, so a character count
  // is just noise (e.g. the contact-request view modal renders every value
  // through a read-only Input).
  const isReadOnly = Boolean(
    childProps?.readOnly || childProps?.disabled || childProps?.plaintext
  );
  const rawValue = childProps?.value;
  const valueString =
    typeof rawValue === 'string' || typeof rawValue === 'number'
      ? String(rawValue)
      : null;
  const valueLength = valueString !== null ? valueString.length : null;
  const rawMaxLength = childProps?.maxLength;
  const parsedMaxLength =
    typeof rawMaxLength === 'number'
      ? rawMaxLength
      : typeof rawMaxLength === 'string' && rawMaxLength.length > 0
        ? Number(rawMaxLength)
        : undefined;
  const counterLimit =
    typeof maxLength === 'number' ? maxLength : parsedMaxLength;
  const hasHint = Boolean(hint);
  const shouldShowCounter =
    showCounter &&
    valueLength !== null &&
    !isDateField &&
    !isNumericField &&
    !isNonTextControl &&
    !isReadOnly &&
    !hasHint;
  const counterNode = shouldShowCounter ? (
    counterLimit ? (
      <span className="form-modal__hint-row">
        <span>Рекомендовано до {counterLimit} символів</span>
        <span>
          {valueLength}/{counterLimit}
        </span>
      </span>
    ) : (
      <span>{valueLength} символів</span>
    )
  ) : null;
  const hintContent =
    hint && counterNode ? (
      <div className="form-modal__hint-stack">
        <div>{hint}</div>
        <div>{counterNode}</div>
      </div>
    ) : (
      (hint ?? counterNode)
    );
  const control =
    React.isValidElement(children) && (id || errorId)
      ? React.cloneElement(children as ReactElement, {
          ...(id ? { id } : {}),
          ...(errorId ? { 'aria-describedby': errorId } : {}),
          'aria-invalid': !!error,
        })
      : children;

  return (
    <div className="form-modal__field">
      <label htmlFor={id} className="form-modal__label">
        <span>{label}</span>
        {required ? <span className="form-modal__required">*</span> : null}
      </label>
      {control}
      {hintContent ? (
        <div className="form-modal__hint">{hintContent}</div>
      ) : null}
      {error ? (
        <span id={errorId} className="form-modal__error">
          {error}
        </span>
      ) : null}
    </div>
  );
}
